import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Cookie, Shield, Check } from 'lucide-react';

const CookieConsent = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [showSettings, setShowSettings] = useState(false);

    // Cookie settings state
    const [preferences, setPreferences] = useState({
        essential: true, // Always true
        analytics: true,
        marketing: true
    });

    useEffect(() => {
        // Check if user already gave consent
        const consent = localStorage.getItem('seltz_cookie_consent');
        if (!consent) {
            // Small delay for better UX
            const timer = setTimeout(() => setIsVisible(true), 1500);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleAcceptAll = () => {
        const allAccepted = { essential: true, analytics: true, marketing: true };
        localStorage.setItem('seltz_cookie_consent', JSON.stringify(allAccepted));
        setIsVisible(false);
    };

    const handleRejectNonEssential = () => {
        const essentialOnly = { essential: true, analytics: false, marketing: false };
        localStorage.setItem('seltz_cookie_consent', JSON.stringify(essentialOnly));
        setIsVisible(false);
    };

    const handleSavePreferences = () => {
        localStorage.setItem('seltz_cookie_consent', JSON.stringify(preferences));
        setIsVisible(false);
    };

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] w-[calc(100%-32px)] max-w-3xl pointer-events-none">
            <div className="pointer-events-auto bg-white rounded-2xl shadow-[0_12px_48px_rgba(0,0,0,0.18)] border border-gray-100 overflow-hidden">
                {!showSettings ? (
                    // Main Banner - Ultra Compact
                    <div className="p-4 sm:p-5 flex flex-col md:flex-row gap-4 items-center justify-between relative pl-4 pr-10 md:pr-4">
                        <button
                            onClick={() => setIsVisible(false)}
                            className="absolute top-2 right-2 text-gray-400 hover:text-[#0a0a0a] transition-colors p-1 md:hidden"
                            aria-label="Închide"
                        >
                            <X size={16} />
                        </button>

                        <div className="flex-1 flex items-center gap-3 w-full self-start md:self-auto">
                            <div className="w-8 h-8 shrink-0 rounded-full bg-orange-100 flex items-center justify-center text-orange-600">
                                <Cookie size={16} />
                            </div>
                            <p className="text-[#0a0a0a] text-xs sm:text-[13px] leading-tight" style={{ fontFamily: 'Nunito, sans-serif' }}>
                                Folosim cookie-uri pentru a-ți oferi cea mai bună experiență.
                                <Link to="/privacy" className="underline font-extrabold ml-1">Detalii aici.</Link>
                            </p>
                        </div>

                        <div className="flex flex-row gap-2 w-full md:w-auto shrink-0 justify-end md:items-center">
                            <button
                                onClick={() => setShowSettings(true)}
                                className="px-3 py-2 rounded-xl border border-gray-200 text-gray-600 hover:text-[#0a0a0a] font-bold text-[11px] sm:text-xs hover:bg-gray-50 transition-colors flex-1 md:flex-none text-center"
                                style={{ fontFamily: 'Nunito, sans-serif' }}
                            >
                                Setări
                            </button>
                            <button
                                onClick={handleRejectNonEssential}
                                className="px-3 py-2 rounded-xl bg-gray-100 text-[#0a0a0a] font-bold text-[11px] sm:text-xs hover:bg-gray-200 transition-colors flex-1 md:flex-none text-center whitespace-nowrap"
                                style={{ fontFamily: 'Nunito, sans-serif' }}
                            >
                                Doar Necesare
                            </button>
                            <button
                                onClick={handleAcceptAll}
                                className="px-4 py-2 rounded-xl bg-[#0a0a0a] text-white font-bold text-[11px] sm:text-xs hover:opacity-80 transition-opacity flex-1 md:flex-none text-center whitespace-nowrap"
                                style={{ fontFamily: 'Nunito, sans-serif' }}
                            >
                                Acceptă Toate
                            </button>
                            <button
                                onClick={() => setIsVisible(false)}
                                className="hidden md:flex ml-1 text-gray-400 hover:text-[#0a0a0a] transition-colors p-1"
                                aria-label="Închide"
                            >
                                <X size={20} />
                            </button>
                        </div>
                    </div>
                ) : (
                    // Settings Modal content - Compact
                    <div className="p-4 sm:p-5 max-h-[85vh] flex flex-col">
                        <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-4 shrink-0">
                            <div className="flex items-center gap-2">
                                <Shield size={18} className="text-blue-600" />
                                <h3 className="text-[#0a0a0a] font-extrabold text-[15px] sm:text-base leading-tight" style={{ fontFamily: 'Nunito, sans-serif' }}>Setări Cookie-uri</h3>
                            </div>
                            <button
                                onClick={() => setShowSettings(false)}
                                className="text-gray-400 hover:text-[#0a0a0a] transition-colors p-1 bg-white rounded-full"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="space-y-4 overflow-y-auto pr-2 custom-scrollbar flex-1 min-h-0">
                            {/* Essential */}
                            <div className="flex items-start justify-between gap-3">
                                <div className="pr-2">
                                    <h4 className="text-[#0a0a0a] font-black text-[13px] sm:text-sm mb-0.5">Strict Necesare</h4>
                                    <p className="text-gray-600 text-[10px] sm:text-[11px] leading-snug">
                                        Pentru navigare, securitate sau coșul de cumpărături. Nu se pot dezactiva.
                                    </p>
                                </div>
                                <div className="bg-gray-200 text-gray-500 text-[10px] font-bold px-2 py-0.5 rounded shrink-0 whitespace-nowrap">
                                    Activat
                                </div>
                            </div>

                            {/* Analytics */}
                            <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-50">
                                <div className="pr-2">
                                    <h4 className="text-[#0a0a0a] font-black text-[13px] sm:text-sm mb-0.5">Analitice</h4>
                                    <p className="text-gray-600 text-[10px] sm:text-[11px] leading-snug">
                                        Analizăm anonim modul de utilizare a site-ului.
                                    </p>
                                </div>
                                <button
                                    onClick={() => setPreferences({ ...preferences, analytics: !preferences.analytics })}
                                    className={`w-10 h-5 rounded-full transition-colors relative shrink-0 ${preferences.analytics ? 'bg-[#0a0a0a]' : 'bg-gray-200'}`}
                                >
                                    <div className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${preferences.analytics ? 'translate-x-5' : 'translate-x-0'}`} />
                                </button>
                            </div>

                            {/* Marketing */}
                            <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-50">
                                <div className="pr-2">
                                    <h4 className="text-[#0a0a0a] font-black text-[13px] sm:text-sm mb-0.5">Marketing</h4>
                                    <p className="text-gray-600 text-[10px] sm:text-[11px] leading-snug">
                                        Pentru reclame relevante și campanii.
                                    </p>
                                </div>
                                <button
                                    onClick={() => setPreferences({ ...preferences, marketing: !preferences.marketing })}
                                    className={`w-10 h-5 rounded-full transition-colors relative shrink-0 ${preferences.marketing ? 'bg-[#0a0a0a]' : 'bg-gray-200'}`}
                                >
                                    <div className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${preferences.marketing ? 'translate-x-5' : 'translate-x-0'}`} />
                                </button>
                            </div>
                        </div>

                        <div className="mt-5 pt-3 border-t border-gray-100 flex flex-row justify-end gap-2 shrink-0">
                            <button
                                onClick={() => setShowSettings(false)}
                                className="px-4 py-2 rounded-xl border border-gray-200 text-[#0a0a0a] font-bold text-xs hover:bg-gray-50 transition-colors text-center w-full sm:w-auto"
                                style={{ fontFamily: 'Nunito, sans-serif' }}
                            >
                                Înapoi
                            </button>
                            <button
                                onClick={handleSavePreferences}
                                className="px-4 py-2 rounded-xl bg-[#0a0a0a] text-white font-bold text-xs hover:opacity-80 transition-opacity flex items-center justify-center gap-1.5 w-full sm:w-auto"
                                style={{ fontFamily: 'Nunito, sans-serif' }}
                            >
                                <Check size={14} /> Salvează Preferințele
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CookieConsent;
